/* ═══════════════════════════════════════════════════
   NATIVE AUDIO — Capacitor native only
   Wraps HTML5 Audio with background playback support
   and Media Session API for lock screen controls.

   On native: uses HTML5 Audio element (which Capacitor
   Android wraps with proper background service support)
   On web: falls back to howler.js via AudiobookPlayer
═══════════════════════════════════════════════════ */

import { Capacitor } from '@capacitor/core';

/**
 * NativeAudioPlayer — wraps HTML5 Audio for Capacitor native builds.
 * Provides the same interface AudiobookPlayer expects.
 */
export class NativeAudioPlayer {
  constructor({ src, volume = 1, rate = 1, onLoad, onEnd, onLoadError, onPlayError }) {
    this._audio = new Audio();
    this._audio.preload = 'auto';
    this._audio.volume = volume;
    this._audio.playbackRate = rate;
    this._loaded = false;
    this._destroyed = false;

    this._audio.addEventListener('loadedmetadata', () => {
      this._loaded = true;
      if (onLoad) onLoad();
    });

    this._audio.addEventListener('ended', () => {
      if (onEnd) onEnd();
    });

    this._audio.addEventListener('error', (e) => {
      if (!this._loaded) {
        if (onLoadError) onLoadError(e);
      } else {
        if (onPlayError) onPlayError(e);
      }
    });

    this._audio.src = src;
  }

  play() {
    if (this._destroyed) return;
    return this._audio.play().catch(err => {
      console.warn('[NativeAudioPlayer] play() error:', err);
    });
  }

  pause() {
    if (this._destroyed) return;
    this._audio.pause();
  }

  stop() {
    if (this._destroyed) return;
    this._audio.pause();
    this._audio.currentTime = 0;
  }

  seek(seconds) {
    if (this._destroyed) return;
    if (typeof seconds === 'number') {
      this._audio.currentTime = seconds;
    }
    return this._audio.currentTime;
  }

  duration() {
    return this._audio.duration || 0;
  }

  playing() {
    return !this._audio.paused && !this._audio.ended;
  }

  volume(val) {
    if (typeof val === 'number') {
      this._audio.volume = Math.max(0, Math.min(1, val));
    }
    return this._audio.volume;
  }

  rate(val) {
    if (typeof val === 'number') {
      this._audio.playbackRate = val;
    }
    return this._audio.playbackRate;
  }

  unload() {
    this._destroyed = true;
    this._audio.pause();
    this._audio.removeAttribute('src');
    this._audio.load(); // Release resources
  }
}

/**
 * Set up Media Session API for lock screen controls.
 * Call this whenever playback state changes.
 */
export function updateMediaSession({ title, artist, album, coverUrl, isPlaying, duration, position, onPlay, onPause, onSeekBackward, onSeekForward, onPreviousTrack, onNextTrack }) {
  if (!('mediaSession' in navigator)) return;

  navigator.mediaSession.metadata = new MediaMetadata({
    title: title || 'Audiobook',
    artist: artist || '',
    album: album || '',
    artwork: coverUrl ? [
      { src: coverUrl, sizes: '256x256', type: 'image/jpeg' },
      { src: coverUrl, sizes: '512x512', type: 'image/jpeg' },
    ] : [],
  });

  navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';

  // Set position state for the scrub bar
  if (duration > 0 && typeof position === 'number') {
    try {
      navigator.mediaSession.setPositionState({
        duration: duration,
        playbackRate: 1,
        position: Math.min(position, duration),
      });
    } catch {
      // Some browsers don't support setPositionState
    }
  }

  // Action handlers
  const handlers = {
    play: onPlay,
    pause: onPause,
    seekbackward: onSeekBackward,
    seekforward: onSeekForward,
    previoustrack: onPreviousTrack,
    nexttrack: onNextTrack,
  };

  for (const [action, handler] of Object.entries(handlers)) {
    try {
      if (handler) {
        navigator.mediaSession.setActionHandler(action, handler);
      } else {
        navigator.mediaSession.setActionHandler(action, null);
      }
    } catch {
      // Action not supported
    }
  }
}

/**
 * Clear Media Session when player is destroyed.
 */
export function clearMediaSession() {
  if (!('mediaSession' in navigator)) return;
  navigator.mediaSession.metadata = null;
  navigator.mediaSession.playbackState = 'none';
  const actions = ['play', 'pause', 'seekbackward', 'seekforward', 'previoustrack', 'nexttrack'];
  for (const action of actions) {
    try {
      navigator.mediaSession.setActionHandler(action, null);
    } catch { /* ignore */ }
  }
}

/**
 * Check if we should use native audio (Capacitor native platform).
 */
export function shouldUseNativeAudio() {
  return Capacitor.isNativePlatform();
}
